<template>
	<section class="offline-more-popup">
		<div class="offline-more-popup__info">
			{{ translate("ui.watch_ad_to_get_extra_rewards") }}
		</div>
		<div class="offline-more-popup__info smaller">
			{{ `(${translate("ui.resources_maxed_warning")})` }}
		</div>
		<div class="offline-more-popup__rewards">
			<RaggedBoxBackground
				slug="offline-rewards-box-bg"
				:skin="{ stopColor1: '#f2dcb5', stopColor2: '#fff3dd' }"
			/>
			<div
				class="offline-rewards"
				v-if="rewards && Object.keys(rewards).length"
			>
				<div class="offline-rewards__heading">
					{{ translate("ui.extra_rewards") }}
				</div>
				<template
					v-for="([icon, value], index) in Object.entries(rewards)"
					:key="index"
				>
					<div class="offline-reward" v-if="value > 0">
						<span class="offline-reward__value">
							{{
								`${value >= 0 ? "+" : ""}${formatNumber(value)}`
							}}
						</span>
						<AppMainIcon
							class="offline-reward__icon"
							:iconName="icon.toLowerCase()"
							:iconSize="20"
						/>
					</div>
				</template>
			</div>
		</div>
		<div class="offline-more-popup__buttons">
			<div class="offline-more-popup__btns">
				<AppTextButton
					class="offline-more-popup__btn"
					:disabled="isPending"
					:type="StatusTypes.PRIMARY"
					:text="translate('button.watch_ad')"
					@click="handleAdButtonClick"
				/>
				<ToastMessage
					width="18rem"
					height="2rem"
					message="no_ad_available"
					:showMessage="showToast"
				/>
			</div>
			<DecorativeTextDivider :label="translate('ui.or')" />
			<AppTextButton
				class="offline-more-popup__btn"
				:disabled="isPending || resourcesStore.adSkipper <= 0"
				:type="StatusTypes.SUCCESS"
				:text="translate('button.skip_ad')"
				@click="useAdSkipper"
			/>
			<div class="offline-more-popup__skippers">
				{{ `${translate(`ui.${ResourceType.AD_SKIPPER}_left`)}: ` }}
				<span>
					{{ formatNumber(resourcesStore.adSkipper) }}
				</span>
				<span :class="iconClassName(ResourceType.AD_SKIPPER, 16)" />
			</div>
		</div>
	</section>
</template>

<script lang="ts" setup>
import AppMainIcon from "@/components/AppMainIcon.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import DecorativeTextDivider from "@/components/SVG/DecorativeTextDivider.vue";
import RaggedBoxBackground from "@/components/SVG/RaggedBoxBackground.vue";
import ToastMessage from "@/components/ToastMessage.vue";
import {
	IS_DEV,
	OFFLINE_INCOME_DEFAULT_MULTIPLIER,
	OFFLINE_INCOME_WITH_AD_MULTIPLIER,
} from "@/config/constants";
import { ResourceType } from "@/game/entities/config/resources";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useSegmentManager from "@/hooks/useSegmentManager";
import useSocketService from "@/hooks/useSocketService";
import useTileRepository from "@/hooks/useTileRepository";
import Communicator from "@/libs/AppCommunicatior";
import { AdAnalyticsClickSource, AdAnalyticsType } from "@/libs/SocketService";
import { usePopupStore } from "@/store/usePopupStore";
import useResourcesStore from "@/store/useResourcesStore";
import { StatusTypes } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { iconClassName } from "@/utils/iconClassName";
import { translate } from "@/utils/translate";
import { markRaw, onMounted, onUnmounted, ref } from "vue";

const props = defineProps({
	onClaim: {
		type: Function,
		required: false,
	},
});

const rewards = ref<Partial<Record<ResourceType, number>>>({});
const emitter = markRaw(useEmitter());

const popupStore = markRaw(usePopupStore());

const communicator = markRaw(Communicator.getInstance());
const socketService = markRaw(useSocketService());
const resourcesStore = useResourcesStore();
const tilerepo = markRaw(useTileRepository());
const segmentManager = markRaw(useSegmentManager());
segmentManager.run(true);

const showToast = ref<boolean>(false);
const isToastAnimating = ref<boolean>(false);
const isPending = ref<boolean>(false);

const finishAndReturn = (): void => {
	if (props.onClaim) props.onClaim();
};

const handleAdButtonClick = async (): Promise<void> => {
	if (isPending.value) return;

	if (IS_DEV) {
		isPending.value = true;
		try {
			window.rewardedVideoEarned();
			finishAndReturn();
		} finally {
			isPending.value = false;
		}
		return;
	}

	if (!communicator.isRewardedAdAvailable) {
		if (isToastAnimating.value) return;

		isToastAnimating.value = true;
		showToast.value = true;
		setTimeout((): void => {
			showToast.value = false;
			isToastAnimating.value = false;
		}, 3000);
	} else {
		await playAd();
	}
};

const useAdSkipper = async (): Promise<void> => {
	if (isPending.value || resourcesStore.adSkipper <= 0) return;
	isPending.value = true;

	try {
		resourcesStore.decrease(ResourceType.AD_SKIPPER, 1, "used for offer");
		finishAndReturn();
		popupStore.hidePopup();
	} catch (error) {
		console.error("Failed to use ad skipper", error);
	} finally {
		isPending.value = false;
	}
};

const recalculateRewards = (): void => {
	const rewardsWithoutMultiplier = tilerepo.handleGlobalOfflineProduction(
		OFFLINE_INCOME_DEFAULT_MULTIPLIER,
	);

	const rewardsWithMultiplier = tilerepo.handleGlobalOfflineProduction(
		OFFLINE_INCOME_WITH_AD_MULTIPLIER,
	);

	rewards.value = Object.fromEntries(
		(
			Object.entries(rewardsWithoutMultiplier) as Array<
				[ResourceType, number]
			>
		).map(([key]: [ResourceType, number]) => [
			key,
			(rewardsWithMultiplier[key] || 0) -
				(rewardsWithoutMultiplier[key] || 0),
		]),
	) as Partial<Record<ResourceType, number>>;
};

recalculateRewards();

const playAd = async (): Promise<void> => {
	if (isPending.value) return;
	isPending.value = true;

	try {
		if (IS_DEV) {
			finishAndReturn();
			return;
		}
		await socketService.emitAdAnalyticsSave({
			type: AdAnalyticsType.OFFLINE_INCOME_AD,
			clickSource: AdAnalyticsClickSource.POPUP,
		});
		communicator.playRewardedAd();

		finishAndReturn();
	} catch (error) {
		console.error("Failed to execute ad analytics or play ad", error);
	} finally {
		isPending.value = false;
	}
};

const onGameTick = (): void => {
	segmentManager.run(true);
	recalculateRewards();
};

onMounted((): void => {
	emitter.on(EmitterEvents.GameTick, onGameTick);
});

onUnmounted((): void => {
	emitter.off(EmitterEvents.GameTick, onGameTick);
});
</script>

<style lang="scss" scoped>
.offline-more-popup {
	@include flex-col-center;
	padding: 0.5rem;

	&__info {
		margin-top: 0.5rem;
		padding: 0 0.5rem;
		color: #372615;
		font-size: 1rem;
		line-height: 1rem;
		text-align: center;

		&.smaller {
			font-size: 0.875rem;
			line-height: 0.875rem;
		}
	}

	&__rewards {
		position: relative;
		padding: 1rem 0.75rem;
		width: 100%;
		@include flex-col-center;
		margin: 1rem auto;
	}

	&__btns {
		position: relative;
	}

	&__buttons {
		@include flex-col-center;

		.decorative-text-divider {
			margin-top: 1.125rem;
			margin-bottom: 0.75rem;
		}
	}

	&__skippers {
		display: flex;
		align-items: center;
		gap: 0.15rem;
		margin-top: 0.5rem;
		color: #372615;
		font-size: 0.875rem;

		& > span {
			font-weight: bold;
		}
	}

	:deep(.ragged-box-bg) {
		position: absolute;
		z-index: -1;
		filter: drop-shadow(0 0 0.1rem rgba(49, 37, 22, 0.6));
		width: 100%;
		height: 100%;
	}

	.offline-rewards {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 0.5rem;
		width: 80%;

		&__heading {
			width: 100%;
			color: #372615;
			font-weight: bold;
			font-size: 0.875rem;
			text-align: center;
		}
	}

	.offline-reward {
		@include flex-row-center;
		gap: 0.15rem;
		&__value {
			color: #0d6c27;
			font-weight: bold;
			font-size: 1.063rem;
		}
	}
}

// Deep CSS styling to make Vue ToastMessage look like Phaser ToastMessage
:deep(.toast-message) {
	position: absolute !important;
	top: -2.5rem !important;
	left: 50% !important;
	transform: translate(-50%) !important;
}
</style>
